import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import InputField from "../../components/UI/Forms/InputField";
import ErrorMessage from "../../components/UI/Notifications/ErrorMessage";
import Button from "../../components/UI/Buttons/Button";
import SearchableMultiSelect, { OptionItem } from "../../components/UI/Forms/SearchableMultiSelect";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { showToastNotification } from "../../utility/helper";
import { IModifierCollection, IModifierGroup, IModifierOption } from "../../interfaces/modifier";
import ModifierOptions from "./ModifierOptions";

const ModifierGroupForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const collectionIdParam = searchParams.get("collectionId");
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<any>({ mode: "onBlur", defaultValues: { sortOrder: 0, isActive: true } });

    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [options, setOptions] = useState<IModifierOption[]>([]);
    const [existingGroup, setExistingGroup] = useState<IModifierGroup | null>(null);

    // Parent collections state
    const [collections, setCollections] = useState<IModifierCollection[]>([]);
    const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>(
        collectionIdParam ? [collectionIdParam] : []
    );

    useEffect(() => {
        fetchCollections();
        if (id) fetchGroup(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchCollections = async () => {
        try {
            const res = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}modifier/main?all=true`,
                { method: "GET" }
            );
            const list: IModifierCollection[] = Array.isArray(res) ? res : res?.data || [];
            setCollections(list);
        } catch (error: any) {
            console.error("Failed to fetch modifier collections:", error.message);
        }
    };

    const fetchGroup = async (groupId: string) => {
        try {
            const res: IModifierGroup = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}modifier/${groupId}`,
                { method: "GET" }
            );
            setExistingGroup(res);
            setValue("name", res.name);
            setValue("sortOrder", res.sortOrder ?? 0);
            setValue("isActive", res.isActive ?? true);

            // Extract all collection IDs assigned to this group
            const initialCollectionIds: string[] = [];
            if (res.modifierIds && Array.isArray(res.modifierIds)) {
                initialCollectionIds.push(...res.modifierIds);
            }
            if (res.modifiers && Array.isArray(res.modifiers)) {
                res.modifiers.forEach((m) => {
                    if (m.id && !initialCollectionIds.includes(m.id)) {
                        initialCollectionIds.push(m.id);
                    }
                });
            }
            if (res.modifierId && !initialCollectionIds.includes(res.modifierId)) {
                initialCollectionIds.push(res.modifierId);
            }

            if (initialCollectionIds.length > 0) {
                setSelectedCollectionIds(initialCollectionIds);
            }

            setOptions(res.options || []);
        } catch (error: any) {
            console.error("Failed to fetch modifier group:", error.message);
            showToastNotification("danger", "Failed to load modifier group");
        }
    };

    const onSubmit = async (data: any) => {
        setLoadingButton(true);
        const payload: any = {
            name: data.name,
            internalName: existingGroup?.internalName ?? "",
            description: existingGroup?.description ?? "",
            sortOrder: data.sortOrder ? Number(data.sortOrder) : 0,
            isActive: data.isActive ?? true,
            // modifierIds = array of parent Collection IDs this group belongs to
            modifierIds: selectedCollectionIds,
        };

        try {
            if (id) {
                await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/${id}`, { method: "PATCH", data: payload });
                showToastNotification("success", "Modifier group updated successfully");
                fetchGroup(id);
            } else {
                const res = await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier`, { method: "POST", data: payload });
                showToastNotification("success", "Modifier group created successfully");
                const editUrl = collectionIdParam
                    ? `/modifiers/edit/${res.id}?collectionId=${collectionIdParam}`
                    : `/modifiers/edit/${res.id}`;
                navigate(editUrl);
            }
        } catch (error: any) {
            showToastNotification("danger", error.message || "Failed to save modifier group");
        } finally {
            setLoadingButton(false);
        }
    };

    // Determine back navigation target
    const backUrl = collectionIdParam
        ? `/modifier-collections/edit/${collectionIdParam}`
        : "/modifiers";
    const backLabel = collectionIdParam ? "Back to Collection" : "Back to Modifier Groups";

    const collectionOptions: OptionItem[] = collections.map((c) => ({
        label: c.label,
        value: c.id,
    }));

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <div>
                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-1">
                            {collectionIdParam ? (
                                <>
                                    <li className="breadcrumb-item">
                                        <Link to="/modifier-collections">Modifier Collections</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to={`/modifier-collections/edit/${collectionIdParam}`}>
                                            Collection
                                        </Link>
                                    </li>
                                </>
                            ) : (
                                <li className="breadcrumb-item">
                                    <Link to="/modifiers">Modifier Groups</Link>
                                </li>
                            )}
                            <li className="breadcrumb-item active" aria-current="page">
                                {id ? "Edit" : "Add"}
                            </li>
                        </ol>
                    </nav>
                    <h1 className="app-page-title mb-0">{id ? "Edit Modifier Group" : "Add Modifier Group"}</h1>
                </div>
                <button className="btn btn-outline-secondary text-nowrap" onClick={() => navigate(backUrl)}>
                    <i className="bi bi-arrow-left me-2" /> {backLabel}
                </button>
            </div>

            {/* Basic Information */}
            <div className="app-card app-card-settings shadow-sm p-3 p-md-4 mb-4">
                <div className="app-card-body">
                    <h5 className="fw-bold mb-3">Basic Information</h5>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            <div className="mb-3 col-md-6 col-12">
                                <InputField
                                    type="text"
                                    register={register("name", { required: "Name is required" })}
                                    name="name"
                                    label="Name"
                                    placeholder="Enter modifier group name"
                                />
                                <ErrorMessage message={errors.name?.message?.toString()} />
                            </div>
                            <div className="mb-3 col-sm-6 col-md-3 col-12">
                                <InputField
                                    type="number"
                                    register={register("sortOrder")}
                                    name="sortOrder"
                                    label="Sort Order"
                                    placeholder="0"
                                />
                            </div>
                            <div className="mb-3 col-sm-6 col-md-3 col-12 d-flex align-items-center">
                                <div className="form-check mt-0 mt-sm-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="isActive"
                                        {...register("isActive")}
                                    />
                                    <label className="form-check-label" htmlFor="isActive">
                                        Active
                                    </label>
                                </div>
                            </div>
                            {/* Parent Collections Multi-select */}
                            <div className="col-12 mb-3">
                                <SearchableMultiSelect
                                    label="Assign to Modifier Collections"
                                    placeholder="Search & select Modifier Collections..."
                                    options={collectionOptions}
                                    selectedValues={selectedCollectionIds}
                                    onChange={setSelectedCollectionIds}
                                    helpText="Select one or multiple Modifier Collections this group belongs to."
                                />
                            </div>
                        </div>
                        <div className="col-12 mt-2 d-grid d-sm-flex justify-content-sm-end">
                            <Button
                                loading={loadingButton}
                                className="btn btn-primary text-white px-5"
                                type="submit"
                                text={id ? "Update" : "Save"}
                            />
                        </div>
                    </form>
                </div>
            </div>

            {/* Modifier Options — only after the group exists */}
            {id ? (
                <div className="app-card app-card-settings shadow-sm p-3 p-md-4">
                    <div className="app-card-body">
                        <h5 className="fw-bold mb-3">Modifier Options</h5>
                        <ModifierOptions
                            modifierGroupId={id}
                            options={options}
                            onOptionAdded={() => fetchGroup(id)}
                        />
                    </div>
                </div>
            ) : (
                <div className="alert alert-info">
                    Save the modifier group first to start adding its options.
                </div>
            )}
        </Layout>
    );
};

export default ModifierGroupForm;
